import { Slot } from "@radix-ui/react-slot";
import { cva, VariantProps } from "class-variance-authority";
import {
    ButtonHTMLAttributes,
    DetailedHTMLProps,
    forwardRef,
    ReactNode,
} from "react";

type DefaultButtonProps = {
    /** counters the button padding with negative margin */
    bleed?: boolean;
    /** Change the component to the HTML tag or custom component of the only child.
     * This will merge the original component props with the props of the supplied
     * element/component and change the underlying DOM node. */
    asChild?: boolean;
} & VariantProps<typeof buttonStyles>;

type IconButtonProps = {
    variant: "icon";
    asChild?: never;
    bleed?: never;
} & VariantProps<typeof iconButtonStyles>;

type ButtonProps = DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
> & {
    className?: string;
    children?: ReactNode;
} & (DefaultButtonProps | IconButtonProps);

const buttonBaseStyles = cva([
    "inline-flex items-center justify-center gap-2",
    "group whitespace-nowrap rounded transition-colors",
    "focus:outline-none disabled:cursor-not-allowed",
]);

const buttonStyles = cva(buttonBaseStyles(), {
    variants: {
        variant: {
            pill: [
                "rounded-full bg-background-lighter border border-primary-gray px-4 py-1.5 text-primary-text shadow",
                "hover:brightness-95 active:brightness-90 active:shadow-none active:top-[0.05rem] transition-all relative",
                "focus:brightness-90 focus:shadow-none focus:top-[0.05rem]",
                "disabled:text-primary-text/40 disabled:brightness-100 disabled:grayscale text-sm",
            ],
            pill2: [
                "rounded-xl bg-transparent border border-primary-gray px-4 py-1.5 text-primary-text/80 shadow",
                "hover:border-primary-text/40 active:brightness-90 active:shadow-none active:top-[0.05rem] transition-all relative",
                "focus:border-primary-text/40 focus:shadow-none focus:top-[0.05rem]",
                "disabled:text-primary-text/40 disabled:brightness-100 disabled:grayscale text-sm",
            ],
            widget: [
                "w-28 border-l border-background-lighter bg-background-lighter text-primary-accent hover:bg-background-lighter active:bg-primary-text/20 focus:bg-primary-text/20 disabled:bg-background-secondary flex items-center justify-center h-full",
            ],
            simple: [
                "rounded-full bg-background-lighter p-1 shadow-lg hover:brightness-110 active:brightness-125 focus:brightness-125",
            ],
            primary: [
                "!bg-primary text-primary-text ring-primary-accent hover:brightness-90 active:brightness-75 focus:brightness-75",
                "focus:ring disabled:bg-primary/30 disabled:text-background-lightest",
            ],
            gradient: [
                "rounded-xl bg-gradient-to-r from-consolaPurple-light to-consolaPurple-dark",
                "text-primary-text hover:brightness-90 active:brightness-75 focus:brightness-75 disabled:text-background-lightest",
                "disabled:brightness-50",
            ],
            danger: [
                "bg-red-400 text-primary-text hover:bg-red-500 active:bg-red-600 focus:bg-red-600",
            ],
            success: [
                "bg-green-500 text-primary-text hover:bg-green-600 active:bg-green-700 focus:bg-green-700 disabled:bg-green-900/25",
            ],
            ghost: ["bg-transparent"],
        },
        size: {
            sm: "py-1 px-2",
            md: "py-2 px-4",
            lg: "py-3 px-6",
        },
        bleed: { true: "", false: "" },
    },
    compoundVariants: [
        { size: "sm", bleed: true, class: "-my-1 -mx-2" },
        { size: "md", bleed: true, class: "-my-2 -mx-4" },
        { size: "lg", bleed: true, class: "-my-3 -mx-6" },
    ],
    defaultVariants: { variant: "primary", size: "md" },
});

const iconButtonStyles = cva(
    buttonBaseStyles({
        class: "text-low hover:bg-tertiary outline-border hover:outline-2",
    }),
    {
        variants: {
            size: {
                sm: "p-1",
                md: "p-2",
                lg: "p-3",
            },
        },
    },
);

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ variant = "primary", size = "md", className, ...props }, ref) => {
        if (variant === "icon")
            return (
                <button
                    ref={ref}
                    {...props}
                    className={`${iconButtonStyles({ size })} ${className}`}
                />
            );

        const { bleed, asChild, ...baseProps } = props;
        const Component = asChild ? Slot : "button";

        return (
            <Component
                // @ts-ignore
                ref={ref}
                {...baseProps}
                className={`${buttonStyles({
                    variant,
                    size,
                    bleed,
                })} ${className}`}
            />
        );
    },
);

Button.displayName = "Button";

export default Button;
